import React, { useState, useRef, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import * as XLSX from 'xlsx';
import {
    Upload,
    Download,
    X,
    FileText,
    AlertCircle,
    CheckCircle,
    Loader2,
    AlertTriangle,
    FileSpreadsheet,
    Copy
} from 'lucide-react';
import Button from '../../../components/common/Button/Button';
import jobService from '../../../api/services/jobService';
import departmentRoleService from '../../../api/services/departmentRoleService';
import locationService from '../../../api/services/locationService';
import { experienceService } from '../../../api/crm/config/configService';
import { toast } from 'react-hot-toast';

const EXCEL_MIME_TYPES = [
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'application/vnd.ms-excel'
];

const isExcelFile = (file) => {
    if (!file) return false;
    return /\.(xlsx|xls)$/i.test(file.name) || EXCEL_MIME_TYPES.includes(file.type);
};

const buildNormalizedJobRows = (rows) => rows.map((row) => {
    const normalizedRow = {};

    Object.entries(row || {}).forEach(([key, value]) => {
        const normalizedKey = String(key).trim();
        normalizedRow[normalizedKey] = value == null ? '' : String(value).trim();
    });

    return normalizedRow;
});

const parseExcelFile = (selectedFile) => new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
        try {
            const data = event.target?.result;

            if (!data) {
                reject(new Error('Failed to read Excel file'));
                return;
            }

            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames?.[0];

            if (!firstSheetName) {
                reject(new Error('Excel file must contain at least one sheet'));
                return;
            }

            const worksheet = workbook.Sheets[firstSheetName];
            const rows = XLSX.utils.sheet_to_json(worksheet, { defval: '', raw: false });

            if (!Array.isArray(rows) || rows.length === 0) {
                reject(new Error('Excel file is empty'));
                return;
            }

            resolve(buildNormalizedJobRows(rows));
        } catch (error) {
            reject(error);
        }
    };

    reader.onerror = () => reject(new Error('Failed to read Excel file'));
    reader.readAsArrayBuffer(selectedFile);
});

const downloadExcelTemplate = () => {
    const headers = [
        'title',
        'department',
        'location',
        'type',
        'experience',
        'priority',
        'preferredGender',
        'deadline',
        'salaryMin',
        'salaryMax',
        'status',
        'description',
        'requirements',
        'skills'
    ];

    const examples = [
        'Senior React Developer',
        'Engineering',
        'Mumbai',
        'Full_time',
        '3-5 Years',
        'medium',
        'any',
        '2024-12-31',
        '800000',
        '1200000',
        'Active Job',
        'We are looking for a senior developer to lead our frontend team...',
        'React;Node.js;MongoDB;AWS',
        'JavaScript;TypeScript;Redux'
    ];

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.aoa_to_sheet([headers, examples]);
    worksheet['!cols'] = headers.map((header) => ({ wch: Math.max(header.length, 20) }));
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Job Upload');

    const excelData = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelData], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const url = window.URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `job_bulk_upload_template_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    window.URL.revokeObjectURL(url);
};

const JOB_TYPE_OPTIONS = ['full_time', 'part_time', 'contract', 'internship', 'remote'];
const PRIORITY_OPTIONS = ['high', 'medium', 'low'];
const PREFERRED_GENDER_OPTIONS = ['any', 'male', 'female'];

const renderValueList = (values, emptyText = 'No values available') => {
    if (!values || values.length === 0) {
        return <span className="text-xs text-gray-400">{emptyText}</span>;
    }

    return (
        <div className="flex flex-wrap gap-1.5">
            {values.map((value) => (
                <span
                    key={value}
                    className="px-2 py-0.5 text-xs rounded bg-white border border-gray-200 text-gray-700"
                >
                    {value}
                </span>
            ))}
        </div>
    );
};

/**
 * JobBulkUploadModal - Modal for bulk uploading jobs from Excel
 */
const JobBulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
    const [step, setStep] = useState('upload'); // 'upload', 'preview', 'uploading', 'result'
    const [file, setFile] = useState(null);
    const [parsedJobs, setParsedJobs] = useState([]);
    const [validationResult, setValidationResult] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [skipDuplicates, setSkipDuplicates] = useState(true);
    const fileInputRef = useRef(null);

    const { data: departments = [], isLoading: loadingDepartments } = useQuery({
        queryKey: ['bulkUploadDepartments', 'active'],
        queryFn: async () => {
            const result = await departmentRoleService.getDepartments({ isActive: true });
            let departmentRows = [];

            if (Array.isArray(result)) {
                departmentRows = result;
            } else if (result?.data?.departments && Array.isArray(result.data.departments)) {
                departmentRows = result.data.departments;
            } else if (Array.isArray(result?.data)) {
                departmentRows = result.data;
            }

            return departmentRows
                .filter((dept) => {
                    if (typeof dept === 'string') return true;
                    if (dept?.isActive === undefined || dept?.isActive === null) return true;
                    return dept.isActive === true;
                })
                .map((dept) => {
                    if (typeof dept === 'string') return dept.trim();
                    return String(
                        dept?.name || dept?.departmentName || dept?.title || dept?.department || dept?._id || ''
                    ).trim();
                })
                .filter(Boolean);
        }
    });

    const { data: locations = [], isLoading: loadingLocations } = useQuery({
        queryKey: ['bulkUploadLocations', 'active'],
        queryFn: async () => {
            const result = await locationService.getActiveLocations();

            if (!result?.success) {
                return [];
            }

            let locationRows = [];
            if (Array.isArray(result.data)) {
                locationRows = result.data;
            } else if (Array.isArray(result?.data?.data)) {
                locationRows = result.data.data;
            } else if (Array.isArray(result?.data?.locations)) {
                locationRows = result.data.locations;
            }

            return locationRows
                .map((loc) => {
                    if (typeof loc === 'string') return loc.trim();
                    return String(loc?.name || loc?.label || loc?.value || '').trim();
                })
                .filter(Boolean);
        }
    });

    const { data: experienceLevels = [], isLoading: loadingExperienceLevels } = useQuery({
        queryKey: ['bulkUploadExperienceLevels', 'active'],
        queryFn: async () => {
            const result = await experienceService.getAll({ isActive: true });
            const rows = Array.isArray(result?.data) ? result.data : [];

            return rows
                .filter((exp) => exp?.isActive !== false)
                .map((exp) => String(exp?.name || '').trim())
                .filter(Boolean);
        }
    });

    // Reset state when modal closes
    const handleClose = useCallback(() => {
        setStep('upload');
        setFile(null);
        setParsedJobs([]);
        setValidationResult(null);
        setUploadResult(null);
        setIsUploading(false);
        setIsValidating(false);
        onClose();
    }, [onClose]);

    // Handle file selection
    const handleFileChange = async (e) => {
        const selectedFile = e.target.files?.[0];
        if (!selectedFile) return;

        // Validate file type
        if (!isExcelFile(selectedFile)) {
            toast.error('Please upload an Excel file (.xlsx or .xls)');
            return;
        }

        // Validate file size (max 5MB)
        if (selectedFile.size > 5 * 1024 * 1024) {
            toast.error('File size must be less than 5MB');
            return;
        }

        setFile(selectedFile);

        // Read and parse the file
        try {
            const jobs = await parseExcelFile(selectedFile);

            if (jobs.length > 500) {
                toast.error('Maximum 500 jobs per upload. Please split your file.');
                setFile(null);
                return;
            }

            setParsedJobs(jobs);
            setStep('preview');

            // Auto-validate
            await validateJobs(jobs);
        } catch (error) {
            toast.error(error.message || 'Failed to parse Excel file');
            setFile(null);
        }
    };

    // Validate parsed jobs
    const validateJobs = async (jobs) => {
        setIsValidating(true);
        try {
            const result = await jobService.validateBulkUpload(jobs);
            setValidationResult(result);
        } catch (error) {
            toast.error('Validation failed: ' + (error.message || 'Unknown error'));
        } finally {
            setIsValidating(false);
        }
    };

    // Download template
    const handleDownloadTemplate = async () => {
        try {
            downloadExcelTemplate();
            toast.success('Template downloaded');
        } catch (error) {
            toast.error(error.message || 'Failed to download template');
        }
    };

    // Handle drag and drop
    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const droppedFile = e.dataTransfer?.files?.[0];
        if (droppedFile) {
            const event = { target: { files: [droppedFile] } };
            handleFileChange(event);
        }
    };

    // Perform the actual upload
    const handleUpload = async () => {
        if (parsedJobs.length === 0) return;

        setIsUploading(true);
        setStep('uploading');

        try {
            const result = await jobService.bulkUploadJobs(file, { skipDuplicates });
            setUploadResult(result);
            setStep('result');

            if (result.success) {
                const created = result.data?.created || 0;
                const updated = result.data?.updated || 0;
                const parts = [];
                if (created > 0) parts.push(`${created} created`);
                if (updated > 0) parts.push(`${updated} updated`);
                toast.success(parts.length > 0 ? `Successfully ${parts.join(', ')} jobs` : 'Upload complete');
                if (onSuccess) onSuccess();
            } else {
                toast.error(result.error || 'Upload failed');
            }
        } catch (error) {
            console.error('Bulk upload error:', error);
            toast.error(error.message || 'Upload failed');
            setUploadResult({
                success: false,
                error: error.message || 'Upload failed'
            });
            setStep('result');
        } finally {
            setIsUploading(false);
        }
    };

    // Go back to previous step
    const handleBack = () => {
        if (step === 'preview') {
            setStep('upload');
            setFile(null);
            setParsedJobs([]);
            setValidationResult(null);
        } else if (step === 'result') {
            handleClose();
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 bg-brand/10 rounded-lg">
                            <Upload className="h-5 w-5 text-brand" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">Bulk Upload Jobs</h2>
                            <p className="text-sm text-gray-500">
                                {step === 'upload' && 'Upload an Excel file with job data'}
                                {step === 'preview' && (() => {
                                    const errorCount = validationResult?.data?.failed || 0;
                                    const duplicateCount = validationResult?.data?.duplicates?.length || 0;
                                    const readyCount = Math.max(0, parsedJobs.length - errorCount - duplicateCount);
                                    if (readyCount > 0) {
                                        return `${readyCount} of ${parsedJobs.length} jobs ready for upload`;
                                    } else {
                                        return `${parsedJobs.length} jobs parsed, none ready for upload`;
                                    }
                                })()}
                                {step === 'uploading' && 'Uploading jobs...'}
                                {step === 'result' && 'Upload complete'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-2 hover:bg-blue-100/60 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5 text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    {/* Upload Step */}
                    {step === 'upload' && (
                        <div className="space-y-6">
                            {/* Drag & Drop Zone */}
                            <div
                                className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-brand hover:bg-brand/5 transition-all cursor-pointer"
                                onDragOver={handleDragOver}
                                onDrop={handleDrop}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept=".xlsx,.xls"
                                    onChange={handleFileChange}
                                    className="hidden"
                                />
                                <FileSpreadsheet className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                                <p className="text-lg font-medium text-gray-700 mb-2">
                                    Drag & drop your Excel file here
                                </p>
                                <p className="text-sm text-gray-500 mb-4">
                                    or click to browse (Max 500 jobs, 5MB)
                                </p>
                                <Button variant="outline" icon={<Upload className="h-4 w-4" />}>
                                    Select File
                                </Button>
                            </div>

                            {/* Template Download */}
                            <div className="bg-blue-50 rounded-xl p-4 flex items-start space-x-4">
                                <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                                    <FileText className="h-5 w-5 text-[#0F2942]" />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-sm font-medium text-blue-900 mb-1">
                                        Need a template?
                                    </h4>
                                    <p className="text-sm text-[#0B2033] mb-3">
                                        Download our Excel template with all the required fields and sample data.
                                    </p>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        icon={<Download className="h-4 w-4" />}
                                        onClick={handleDownloadTemplate}
                                        className="border-blue-300 text-[#0B2033] hover:bg-blue-100"
                                    >
                                        Download Template
                                    </Button>
                                </div>
                            </div>

                            {/* Field Reference */}
                            <div className="bg-gray-50 rounded-xl p-4">
                                <h4 className="text-sm font-medium text-gray-900 mb-3">Bulk Upload Instructions (Accepted Values)</h4>
                                <div className="space-y-4 text-sm text-gray-700">
                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Required Columns</p>
                                        <p className="text-sm text-gray-700">title, department, location, type, experience, status, deadline, description</p>
                                    </div>

                                    <div>
                                        <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Optional Columns</p>
                                        <p className="text-sm text-gray-700">salaryMin, salaryMax, preferredGender, requirements, skills, priority</p>
                                    </div>

                                    <div className="pt-2 border-t border-gray-200 space-y-3">
                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Department (Active Only)</p>
                                            {loadingDepartments ? (
                                                <span className="text-xs text-gray-400">Loading active departments...</span>
                                            ) : renderValueList(departments, 'No active departments found')}
                                        </div>

                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Location (Active Only)</p>
                                            {loadingLocations ? (
                                                <span className="text-xs text-gray-400">Loading active locations...</span>
                                            ) : renderValueList(locations, 'No active locations found')}
                                        </div>

                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Job Type</p>
                                            {renderValueList(JOB_TYPE_OPTIONS)}
                                        </div>

                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Experience Level</p>
                                            {loadingExperienceLevels ? (
                                                <span className="text-xs text-gray-400">Loading experience levels...</span>
                                            ) : renderValueList(experienceLevels, 'No active experience levels found')}
                                        </div>

                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Priority</p>
                                            {renderValueList(PRIORITY_OPTIONS)}
                                        </div>

                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Preferred Gender</p>
                                            {renderValueList(PREFERRED_GENDER_OPTIONS)}
                                        </div>

                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Status</p>
                                            {renderValueList(['Active Job', 'Draft Job', 'Closed Job', 'All Job'])}
                                        </div>

                                        <div>
                                            <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Multi-value Format</p>
                                            <p className="text-xs text-gray-600">Use semicolon ( ; ) to separate multiple values in requirements and skills. Example: React;Node;AWS</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Preview Step */}
                    {step === 'preview' && (
                        <div className="space-y-6">
                            {/* File Info */}
                            <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4">
                                <div className="flex items-center space-x-3">
                                    <FileSpreadsheet className="h-8 w-8 text-brand" />
                                    <div>
                                        <p className="font-medium text-gray-900">{file?.name}</p>
                                        <p className="text-sm text-gray-500">{parsedJobs.length} jobs parsed</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        setFile(null);
                                        setParsedJobs([]);
                                        setValidationResult(null);
                                        setStep('upload');
                                    }}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            {/* Validation Status */}
                            {isValidating ? (
                                <div className="flex items-center justify-center p-8">
                                    <Loader2 className="h-8 w-8 animate-spin text-brand" />
                                    <span className="ml-3 text-gray-600">Validating jobs...</span>
                                </div>
                            ) : validationResult && (() => {
                                const totalJobs = parsedJobs.length;
                                const errorCount = validationResult.data?.failed || 0;
                                const duplicateCount = validationResult.data?.duplicates?.length || 0;
                                const readyToUpload = Math.max(0, totalJobs - errorCount - duplicateCount);

                                return (
                                    <div className="space-y-4">
                                        {/* Summary */}
                                        <div className="grid grid-cols-4 gap-3">
                                            <div className="bg-blue-50 rounded-lg p-3 text-center">
                                                <p className="text-xl font-bold text-[#0B2033]">
                                                    {totalJobs}
                                                </p>
                                                <p className="text-xs text-[#0F2942]">Total Parsed</p>
                                            </div>
                                            <div className="bg-red-50 rounded-lg p-3 text-center">
                                                <AlertCircle className="h-5 w-5 text-red-600 mx-auto mb-1" />
                                                <p className="text-xl font-bold text-red-700">
                                                    {errorCount}
                                                </p>
                                                <p className="text-xs text-red-600">Errors</p>
                                            </div>
                                            <div className="bg-yellow-50 rounded-lg p-3 text-center">
                                                <AlertTriangle className="h-5 w-5 text-yellow-600 mx-auto mb-1" />
                                                <p className="text-xl font-bold text-yellow-700">
                                                    {duplicateCount}
                                                </p>
                                                <p className="text-xs text-yellow-600">Duplicates</p>
                                            </div>
                                            <div className={`rounded-lg p-3 text-center ${readyToUpload > 0 ? 'bg-green-50' : 'bg-gray-50'}`}>
                                                <CheckCircle className={`h-5 w-5 mx-auto mb-1 ${readyToUpload > 0 ? 'text-green-600' : 'text-gray-400'}`} />
                                                <p className={`text-xl font-bold ${readyToUpload > 0 ? 'text-green-700' : 'text-gray-500'}`}>
                                                    {readyToUpload}
                                                </p>
                                                <p className={`text-xs ${readyToUpload > 0 ? 'text-green-600' : 'text-gray-500'}`}>Ready to Upload</p>
                                            </div>
                                        </div>

                                        {/* Errors */}
                                        {validationResult.data?.errors?.length > 0 && (
                                            <div className="bg-red-50 rounded-lg p-4">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h4 className="text-sm font-medium text-red-800">Validation Errors</h4>
                                                    <button
                                                        onClick={() => {
                                                            const allErrors = validationResult.data.errors.join('\n');
                                                            navigator.clipboard.writeText(allErrors);
                                                            toast.success(`Copied ${validationResult.data.errors.length} errors to clipboard`);
                                                        }}
                                                        className="flex items-center space-x-1 text-xs text-red-600 hover:text-red-800 bg-red-100 hover:bg-red-200 px-2 py-1 rounded transition-colors"
                                                        title="Copy all errors"
                                                    >
                                                        <Copy className="h-3 w-3" />
                                                        <span>Copy All</span>
                                                    </button>
                                                </div>
                                                <div className="max-h-32 overflow-y-auto space-y-2">
                                                    {validationResult.data.errors.slice(0, 10).map((error, idx) => (
                                                        <div key={idx} className="text-sm text-red-700 bg-red-100/50 p-2 rounded border border-red-200">
                                                            <span className="font-bold mr-1">Row {error.row}:</span>
                                                            {Array.isArray(error.errors) ? error.errors.join(', ') : (error.error || String(error))}
                                                        </div>
                                                    ))}
                                                    {validationResult.data.errors.length > 10 && (
                                                        <p className="text-sm text-red-600 font-medium px-2">
                                                            ... and {validationResult.data.errors.length - 10} more errors
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* Skip Duplicates Option */}
                                        <div className="flex items-center space-x-3 p-4 bg-gray-50 rounded-lg">
                                            <input
                                                type="checkbox"
                                                id="skipDuplicates"
                                                checked={skipDuplicates}
                                                onChange={(e) => setSkipDuplicates(e.target.checked)}
                                                className="h-4 w-4 text-brand rounded border-gray-300 focus:ring-0"
                                            />
                                            <label htmlFor="skipDuplicates" className="text-sm text-gray-700">
                                                Skip duplicate jobs and continue with upload
                                            </label>
                                        </div>

                                        {/* Preview Table */}
                                        <div className="border rounded-lg overflow-hidden">
                                            <div className="bg-gray-50 px-4 py-2 border-b">
                                                <h4 className="text-sm font-medium text-gray-900">Preview (First 5 jobs)</h4>
                                            </div>
                                            <div className="overflow-x-auto">
                                                <table className="min-w-full divide-y divide-gray-200">
                                                    <thead className="bg-transparent" style={{ backgroundColor: "rgba(59, 130, 246, 0.14)" }}>
                                                        <tr>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 uppercase">Title</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 uppercase">Dept</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 uppercase">Location</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 uppercase">Type</th>
                                                            <th className="px-4 py-2 text-left text-xs font-semibold text-gray-900 uppercase">Gender</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="divide-y divide-gray-200">
                                                        {parsedJobs.slice(0, 5).map((job, idx) => (
                                                            <tr key={idx}>
                                                                <td className="px-4 py-2 text-sm text-gray-900 font-medium">{job.title}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{job.department}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{job.location}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500">{job.type}</td>
                                                                <td className="px-4 py-2 text-sm text-gray-500 capitalize">{job.preferredGender || 'Any'}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Uploading Step */}
                    {step === 'uploading' && (
                        <div className="flex flex-col items-center justify-center py-12">
                            <Loader2 className="h-12 w-12 animate-spin text-brand mb-4" />
                            <p className="text-lg font-medium text-gray-900 mb-2">Uploading Jobs...</p>
                            <p className="text-sm text-gray-500">Please wait while we process your data</p>
                        </div>
                    )}

                    {/* Result Step */}
                    {step === 'result' && uploadResult && (
                        <div className="space-y-6">
                            <div className={`text-center py-8 rounded-xl ${uploadResult.success ? 'bg-green-50' : 'bg-red-50'}`}>
                                {uploadResult.success ? (
                                    <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                                ) : (
                                    <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
                                )}
                                <h3 className={`text-xl font-semibold mb-2 ${uploadResult.success ? 'text-green-800' : 'text-red-800'}`}>
                                    {uploadResult.success ? 'Upload Successful!' : 'Upload Failed'}
                                </h3>
                                <p className={`text-sm ${uploadResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                    {uploadResult.message || uploadResult.error}
                                </p>
                            </div>

                            {uploadResult.data && (
                                <div className="grid grid-cols-4 gap-3">
                                    <div className="bg-gray-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-gray-700">{uploadResult.data.total || 0}</p>
                                        <p className="text-sm text-gray-500">Total</p>
                                    </div>
                                    <div className="bg-green-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-green-700">{uploadResult.data.created || 0}</p>
                                        <p className="text-sm text-green-600">Created</p>
                                    </div>
                                    <div className="bg-red-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-red-700">{uploadResult.data.failed || 0}</p>
                                        <p className="text-sm text-red-600">Failed</p>
                                    </div>
                                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                                        <p className="text-2xl font-bold text-yellow-700">{uploadResult.data.skipped || 0}</p>
                                        <p className="text-sm text-yellow-600">Skipped</p>
                                    </div>
                                </div>
                            )}

                            {/* Detailed error list when upload has failures */}
                            {(uploadResult.data?.failed > 0 || !uploadResult.success) && (
                                <div className="mt-4 max-h-48 overflow-y-auto bg-red-50 rounded-lg p-4 border border-red-100">
                                    <h4 className="text-sm font-semibold text-red-800 mb-2 flex items-center">
                                        <AlertCircle className="h-4 w-4 mr-2" />
                                        Upload Details & Errors
                                    </h4>
                                    {uploadResult.error && !uploadResult.data?.errors?.length && (
                                        <p className="text-sm text-red-700 mb-2">{uploadResult.error}</p>
                                    )}
                                    {uploadResult.data?.errors?.length > 0 ? (
                                        <ul className="space-y-2">
                                            {uploadResult.data.errors.map((err, idx) => (
                                                <li key={idx} className="text-sm text-red-700 bg-white/50 p-2 rounded border border-red-200">
                                                    <span className="font-bold mr-1">Row {err.row}:</span>
                                                    {Array.isArray(err.errors) ? err.errors.join(', ') : (err.error || String(err))}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : validationResult?.data?.errors?.length > 0 && uploadResult.data?.failed > 0 ? (
                                        <ul className="space-y-2">
                                            {validationResult.data.errors.map((err, idx) => (
                                                <li key={idx} className="text-sm text-red-700 bg-white/50 p-2 rounded border border-red-200">
                                                    <span className="font-bold mr-1">Row {err.row}:</span>
                                                    {Array.isArray(err.errors) ? err.errors.join(', ') : (err.error || String(err))}
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-red-600 italic">No specific row errors were returned, but some jobs failed to upload.</p>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center space-x-3">
                        {step === 'preview' && (
                            <Button variant="outline" onClick={handleBack}>
                                Back
                            </Button>
                        )}
                        <Button variant="outline" onClick={handleClose}>
                            Cancel
                        </Button>
                    </div>

                    {step === 'preview' && (() => {
                        const errorCount = validationResult?.data?.failed || 0;
                        const validJobsCount = Math.max(0, parsedJobs.length - errorCount - (skipDuplicates ? (validationResult?.data?.duplicates?.length || 0) : 0));
                        const hasNoValidJobs = validJobsCount <= 0;

                        return (
                            <div className="flex items-center space-x-3">
                                {hasNoValidJobs && (
                                    <span className="text-sm text-red-600">No valid jobs to upload</span>
                                )}
                                <Button
                                    variant="primary"
                                    icon={isUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                                    onClick={handleUpload}
                                    disabled={isUploading || isValidating || hasNoValidJobs}
                                    className="bg-brand text-white hover:bg-brand-dark disabled:bg-gray-400"
                                >
                                    {isUploading ? 'Uploading...' : `Upload ${validJobsCount} Jobs`}
                                </Button>
                            </div>
                        );
                    })()}

                    {step === 'result' && (
                        <Button
                            variant="primary"
                            onClick={handleClose}
                            className="bg-brand text-white hover:bg-brand-dark"
                        >
                            Done
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default JobBulkUploadModal;
